// Route monitoring status for the project "Routes" tab (filter pills).
//   inactive = not started
//   active   = underway
//   warning  = underway, but the driver completed nothing by (next stop ETA + 45 min);
//              clears back to active on the next completion (the live arr_time advances)
//   finished = all customer stops done, excluding the end station
// Dutch labels for future i18n: Alle / Inactief / Actief / Afgerond / Waarschuwing.
//
// Kept as its own dependency-free module so it stays unit-testable without importing the
// heavy helpers.js chain (which pulls in DOM libs that break the jsdom test environment).

// A "customer" stop is any stop that is not the start depot, end depot or a break.
// (Mirrors the predicate used by completedStopsCount / the progress bar in helpers.js.)
const isCustomerStop = stop =>
  stop.type !== "start" && stop.type !== "end" && stop.type !== "break";

// A stop is "done" when it is finished or skipped — the same rule the rest of the app
// (progress bar, completedStopsCount) uses, so "finished" here == route progress 100%.
const isStopDone = stop => stop.status === "finished" || stop.status === "skipped";

// Grace period after the next stop's expected delivery time (arr_time) before an
// active-but-stalled route is flagged. FOJW rule: "completed nothing by the next stop's
// ETA + 45 min" => Warning. It clears back to Active on the next completion, because the
// live arr_time of the (new) next stop advances past now (see updateStopFromWebsocket).
export const WARNING_GRACE_SECONDS = 45 * 60;

// True when the driver completed/skipped a customer stop within the last
// WARNING_GRACE_SECONDS — or, if nothing is completed yet, started the route that recently
// (the START stop's finish_time marks the route start). finish_time is seconds-of-day, set
// when a stop is completed. Returns false when no timestamp is available (the caller then
// falls back to the ETA-only rule) and on a seconds-of-day day-boundary wrap (negative delta
// => treat as "long ago", the same intra-day simplification used elsewhere in this module).
const completedWithinGrace = (customerStops, stops, nowSeconds) => {
  const doneTimes = customerStops
    .filter(isStopDone)
    .map(s => s.finish_time)
    .filter(t => t != null);
  const lastProgress = doneTimes.length
    ? Math.max(...doneTimes)
    : stops[0] && stops[0].finish_time != null
    ? stops[0].finish_time
    : null;
  if (lastProgress == null) {
    return false;
  }
  const since = nowSeconds - lastProgress;
  return since >= 0 && since < WARNING_GRACE_SECONDS;
};

// nowSeconds: the current time as seconds-of-day (0–86399), supplied by the caller's clock
// tick (RouteGroups). When it is null the warning state is never returned, so existing
// callers/tests keep the original inactive/active/finished behaviour.
export const getRouteListStatus = (route, nowSeconds = null) => {
  const stops = (route && route.stops) || [];
  const customerStops = stops.filter(isCustomerStop);

  // An empty route (no customer stops) is filtered out of the list upstream; treat as inactive.
  if (customerStops.length === 0) {
    return "inactive";
  }
  // Finished: every customer stop is finished or skipped (the end station is ignored, per spec).
  if (customerStops.every(isStopDone)) {
    return "finished";
  }
  // "started" mirrors the app's own convention (DraggablesWrapper.js, DragList.js):
  // first/start stop finished, or route-level in_progress. start_timestamp is deliberately
  // not used (the app never uses it for started-ness; it risks a 0-vs-null false positive).
  const started =
    (stops[0] && stops[0].status === "finished") ||
    route.status === "in_progress";
  if (!started) {
    return "inactive";
  }
  // Warning: underway, but the driver has completed nothing by the next stop's expected
  // delivery time + grace. The "next stop" is the first not-done customer stop in visiting
  // order; its arr_time is the live ETA (the orange clock) the rule keys off.
  if (nowSeconds != null) {
    const nextStop = customerStops.find(stop => !isStopDone(stop));
    const etaOverdue =
      nextStop &&
      nextStop.arr_time != null &&
      nowSeconds >= nextStop.arr_time + WARNING_GRACE_SECONDS;
    // Suppress when the driver completed a stop within the grace window: recent progress
    // means "not stalled" (FOJW's "completes a stop => back to Active"), which keeps a stale
    // next-stop ETA (e.g. the driver worked a different order) from tripping a false warning.
    if (etaOverdue && !completedWithinGrace(customerStops, stops, nowSeconds)) {
      return "warning";
    }
  }
  return "active";
};
